import React from 'react'
import { leagueIds } from 'src/constants'
import { networkLogos } from 'src/constants/networkLogos'
import { isUSFreeTrial } from 'src/modules/utils/constants'

export const spectrumData = {
  competitor: 'Spectrum',
  competitorColor: '#ffffff',
  heroData: {
    title: 'Looking for the best streaming service?',
    subtitle:
      'Compare Fubo streaming service and Spectrum cable TV service on price, features, channels and add-ons to see which is better for you.',
  },
  generalCompareData: {
    heading: 'Compare Fubo vs. Spectrum Cable TV',
    subheading:
      "Here's a general look at both to help you determine which is the best option for live TV entertainment.",
    listItems: ['Channel Count', 'Cloud DVR', 'Simultaneous Streams', 'Starting Price'],
    fuboList: ['200+', 'Unlimited hours', 'Up to 10', '$73.99/mo.*'],
    competitorList: [
      '150+',
      'Limited storage (additional monthly fee)',
      'Up to 3 outside home',
      '$100/mo.*',
    ],
    note: '*Prices may vary with taxes and fees. Prices subject to change. Spectrum pricing may include additional equipment rental and broadcast TV fees.',
  },
  prosConsData: {
    heading: 'Pros and Cons: Fubo vs. Spectrum Cable TV',
    content: (
      <>
        <p>
          When it comes to watching live TV, Fubo and Spectrum offer two very different approaches.
          Fubo is a modern, sports-first streaming service designed for flexibility and
          affordability, while Spectrum is a traditional cable service with a wide range of channels
          but higher costs and additional fees.
        </p>
        <p>
          Fubo is a premium streaming service with a sports-first approach, offering 200+ live
          channels including 50+ sports networks and extensive local sports coverage. It delivers
          Unlimited Cloud DVR storage, up to 10 simultaneous streams, and cutting-edge features like
          4K HDR streaming and Multiview, which lets you watch up to 4 channels at once on supported
          devices. Eligible plans also include ESPN Unlimited, providing access to additional live
          events and original content. Fubo's no-contract model allows users to cancel anytime
          without penalties, making it ideal for cord cutters seeking flexibility.
        </p>
        <p>
          Spectrum’s Select Signature cable plan, on the other hand, provides a traditional cable TV
          experience with 150+ channels and some DVR capabilities that require additional monthly
          fees. It comes with significant limitations, including mandatory cable box equipment,
          installation requirements and additional fees for equipment rental, DVR service, and
          broadcast TV charges that can drive up monthly costs. While Spectrum offers some regional
          sports networks, it lacks the comprehensive sports coverage that Fubo provides, missing
          key networks like CBS Sports, MLB Network, NBA TV, NHL Network, beIN Sports and more. The
          service also lacks modern streaming flexibility, with limited concurrent streams (only 3
          outside the home) and viewing primarily tied to cable box locations.
        </p>
      </>
    ),
  },
  plansData: {
    heading: 'Compare Fubo Pro plan vs. Spectrum Select Signature',
    subheading:
      'Let’s take a look at the differences between the popular Fubo Pro plan and Spectrum Select Signature.',
    tableData: {
      Plan: ['Pro plan', 'Select Signature'],
      'Live Channels': ['200+', '150+'],
      'Monthly Price': ['$73.99', '$100+ (with 12-month contract)'],
      'Equipment Fees': ['$0', 'Set top box fees, DVR fees'],
      'Hours of DVR storage': ['Unlimited', 'Limited storage (additional monthly fee)'],
      'Free Trial': [isUSFreeTrial ? 'Yes' : 'No', 'No'],
      'Multiple active streams': ['Up to 10', 'Up to 3'],
      '4K HDR streaming': [true, false],
      'No contract required': [true, false],
    },
    note: '*Prices may vary with taxes and fees. Prices subject to change. Spectrum may require additional equipment rental and installation fees.',
  },
  channelsData: {
    heading: 'Compare Fubo Channels vs. Spectrum Select Signature Channels',
    subheading:
      'This channel comparison chart focuses on what’s available among top-watched channels in the Fubo Pro plan vs. Spectrum Select Signature.',
    networksData: [
      { network: networkLogos.abc, fubo: true, competitor: true },
      // TEMPORARY NBC DISPUTE
      // { network: networkLogos.nbc, fubo: true, competitor: true },
      { network: networkLogos.cbs, fubo: true, competitor: true },
      { network: networkLogos.fox, fubo: true, competitor: true },
      { network: networkLogos.espn, fubo: true, competitor: true },
      { network: networkLogos.cbssports, fubo: true, competitor: false },
      { network: networkLogos.fs1, fubo: true, competitor: true },
      { network: networkLogos.bein, fubo: true, competitor: false },
      { network: networkLogos.nflnetwork, fubo: true, competitor: false },
      { network: networkLogos.mlbnetwork, fubo: true, competitor: false },
      { network: networkLogos.nbatv, fubo: true, competitor: false },
      { network: networkLogos.bein, fubo: true, competitor: false },
    ],
  },
  completedChannelsData: {
    heading: 'Complete Channel Comparison',
    subheading:
      'See how the live channel lineups compare between Fubo Pro and Spectrum Select Signature.',
    content: (
      <>
        <p>
          When it comes to watching live TV and sports, it’s clear that Fubo Pro and Spectrum Select
          Signature offer very different approaches. While both services provide a mix of sports,
          entertainment and news channels — the differences in their offerings and value
          propositions are significant.
        </p>
        <p>
          Fubo Pro dominates with its 200+ channels, including 50+ sports networks and channels like
          MLB TV, NBA TV, CBS Sports and lineup of regional sports networks for local team coverage.
          Sports fans won’t find those channels on Spectrum Select Signature. Fubo Pro is the
          ultimate choice for sports enthusiasts, offering superior coverage of local sports, major
          leagues and international competitions. Beyond sports, Fubo Pro includes popular
          entertainment channels like Bravo, Disney Channel and Nickelodeon, plus Spanish-language
          channels like Telemundo, making it perfect for a variety of households.
        </p>
        <p>
          {/* TEMPORARY NBC DISPUTE */}
          {/* Spectrum Select Signature offers a traditional cable experience with 150+ channels
          including major broadcast networks like ABC, CBS, FOX, and NBC.  */}
          Spectrum Select Signature offers a traditional cable experience with 150+ channels
          including major broadcast networks like ABC, CBS, and FOX. However, it falls short in
          sports coverage, lacking premium networks that can be found in Fubo Pro. Spectrum's cable
          TV model requires equipment rentals, installation fees, and additional DVR service fees
          that increase monthly costs. Unlike Fubo's Unlimited Cloud DVR storage, Spectrum's DVR
          comes in various capacities with storage limitations and only allows 3 concurrent streams
          outside the home. For cord cutters seeking value, flexibility and comprehensive sports
          coverage, Fubo Pro is the clear winner.
        </p>
      </>
    ),
  },
  faqData: {
    qaData: [
      {
        id: 'fubo-faq-1',
        question: 'What channels does Fubo Pro have vs. Spectrum Select Signature?',
        answer: (
          <p>
            Fubo Pro includes exclusive sports channels like NFL Network, beIN Sports, and Formula 1
            Channel, which Spectrum Select Signature does not offer. Fubo also has a broader
            entertainment lineup with channels like Bravo and Nickelodeon.
          </p>
        ),
      },
      {
        id: 'fubo-faq-2',
        question: 'Is Fubo Pro better than Spectrum Select Signature for sports?',
        answer: (
          <p>
            Yes, Fubo Pro is better for sports. It offers more sports channels, including exclusive
            options like NFL Network and beIN Sports, as well as extensive local sports coverage and
            regional sports networks.
          </p>
        ),
      },
      {
        id: 'fubo-faq-3',
        question: 'How much does Fubo Pro cost vs. Spectrum Select Signature?',
        answer: (
          <p>
            Fubo Pro costs $73.99 per month, while Spectrum Select Signature costs $100 per month.
            Fubo offers better value per channel and includes features like unlimited DVR storage
            and 4K HDR streaming.
          </p>
        ),
      },
      {
        id: 'fubo-faq-4',
        question: 'Does Fubo Pro require a contract?',
        answer: (
          <p>
            No, Fubo Pro does not require a contract. You can cancel anytime without penalties,
            unlike Spectrum Select Signature, which typically requires a 12-month commitment.
          </p>
        ),
      },
      {
        id: 'fubo-faq-5',
        question: 'Can I stream Fubo Pro on multiple devices?',
        answer: (
          <p>
            Yes, Fubo Pro allows up to 10 simultaneous streams, making it ideal for households with
            multiple viewers. Spectrum Select Signature allows up to 3 simultaneous streams outside
            the home.
          </p>
        ),
      },
    ],
  },
  // Show multiple sports (NFL, NBA, NHL, College Basketball, College Football)
  // Only show games on ESPN, ABC, SEC Network, ACC Network (the Disney networks YouTube TV is missing)
  matchtickerSportId: null, // Not needed when using multiple leagues
  matchtickerLeagueName: 'Multiple Sports',
  matchtickerLeagueId: [
    leagueIds.NFL,
    leagueIds.NBA,
    leagueIds.NHL,
    leagueIds["Men's NCAA Basketball"],
    leagueIds['NCAA Football'],
  ],
}
